import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';
const REFRESH_INTERVAL = 3000;

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 5)  return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return new Date(dateStr).toLocaleTimeString();
}

function formatTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function SuperAdminLiveDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/live-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const json = await res.json();
      setData(json);
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError('Could not reach server');
    }
  };

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, REFRESH_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, []);

  const workerOnline = data?.worker?.online;

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', fontFamily: 'monospace' }}>

      {/* Header */}
      <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/admin/super" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 13 }}>← Dashboard</Link>
          <span style={{ color: '#64748b' }}>|</span>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#f1f5f9' }}>LIVE SYSTEM MONITOR</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {lastRefresh ? `Updated ${timeAgo(lastRefresh)}` : 'Loading...'}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#064e3b', color: '#34d399', padding: '3px 10px',
            borderRadius: 12, fontSize: 12, fontWeight: 600
          }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%', background: '#34d399',
              animation: 'pulse 1.5s infinite'
            }} />
            LIVE · {REFRESH_INTERVAL / 1000}s
          </span>
        </div>
      </div>

      <div style={{ padding: '16px 16px', height: 'calc(100vh - 53px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {error && (
          <div style={{ background: '#450a0a', border: '1px solid #7f1d1d', color: '#fca5a5', padding: '8px 16px', borderRadius: 8, fontSize: 13, flexShrink: 0 }}>
            ⚠ {error} — retrying...
          </div>
        )}

        {/* 3-column main layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 1fr', gap: 12, flex: 1, minHeight: 0 }}>

          {/* Column 1: Stat Cards stacked */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Worker */}
            <div style={{ background: '#1e293b', border: `1px solid ${workerOnline ? '#064e3b' : '#450a0a'}`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 8, letterSpacing: 1 }}>PHOTO WORKER</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{
                  width: 11, height: 11, borderRadius: '50%',
                  background: workerOnline ? '#22c55e' : '#ef4444',
                  boxShadow: workerOnline ? '0 0 8px #22c55e' : 'none', flexShrink: 0
                }} />
                <span style={{ fontSize: 18, fontWeight: 700, color: workerOnline ? '#22c55e' : '#ef4444' }}>
                  {data ? (workerOnline ? 'ONLINE' : 'OFFLINE') : '...'}
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>Last seen: <span style={{ color: '#94a3b8' }}>{timeAgo(data?.worker?.last_seen)}</span></div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Session: <span style={{ color: '#94a3b8' }}>{data?.worker?.jobs_processed ?? '—'} photos</span></div>
            </div>

            {/* Queue */}
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 10, letterSpacing: 1 }}>JOB QUEUE</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[
                  { label: 'Pending',    key: 'pending',    color: '#f59e0b' },
                  { label: 'Processing', key: 'processing', color: '#3b82f6' },
                  { label: 'Done',       key: 'done',       color: '#22c55e' },
                  { label: 'Failed',     key: 'failed',     color: '#ef4444' },
                ].map(({ label, key, color }) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{label}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color }}>{data?.queue?.[key] ?? '—'}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Today */}
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 10, letterSpacing: 1 }}>TODAY</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: '#818cf8', lineHeight: 1 }}>{data?.today?.indexed ?? '—'}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>photos indexed</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#34d399', lineHeight: 1 }}>{data?.today?.visitors ?? '—'}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>visitors registered</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#f472b6', lineHeight: 1 }}>{data?.today?.matches ?? '—'}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>matches found</div>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: Events */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16, overflowY: 'auto' }}>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 14, letterSpacing: 1 }}>EVENTS</div>
          {!data?.events?.length ? (
            <div style={{ color: '#475569', fontSize: 13 }}>No events found.</div>
          ) : (
            data.events.map(ev => {
              const pct = ev.total_photos > 0 ? Math.round((ev.indexed_photos / ev.total_photos) * 100) : 0;
              const statusColor = {
                indexed: '#22c55e', indexing: '#f59e0b', pending: '#64748b',
                failed: '#ef4444', published: '#818cf8'
              };
              return (
                <div key={ev.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #334155' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <Link to={`/admin/super/events/${ev.id}`}
                        style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
                        {ev.name}
                      </Link>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{ev.photographer_name}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {ev.pending_jobs > 0 && (
                        <span style={{ background: '#451a03', color: '#fb923c', fontSize: 11, padding: '2px 8px', borderRadius: 10 }}>
                          {ev.pending_jobs} queued
                        </span>
                      )}
                      <span style={{
                        background: '#0f172a', color: statusColor[ev.indexed_status] || '#64748b',
                        fontSize: 11, padding: '2px 8px', borderRadius: 10, border: `1px solid ${statusColor[ev.indexed_status] || '#334155'}`
                      }}>
                        {ev.indexed_status}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{ background: '#0f172a', borderRadius: 4, height: 6, marginBottom: 8, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 4,
                      background: pct === 100 ? '#22c55e' : '#3b82f6',
                      width: `${pct}%`,
                      transition: 'width 0.5s ease'
                    }} />
                  </div>

                  <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#64748b' }}>
                    <span>Indexed: <b style={{ color: '#94a3b8' }}>{ev.indexed_photos}/{ev.total_photos}</b> ({pct}%)</span>
                    <span>Visitors: <b style={{ color: '#94a3b8' }}>{ev.visitors}</b></span>
                    <span>Matches: <b style={{ color: '#94a3b8' }}>{ev.matches}</b></span>
                  </div>
                </div>
              );
            })
          )}
          </div>

          {/* Column 3: Activity Log */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 12, letterSpacing: 1, flexShrink: 0 }}>LIVE ACTIVITY LOG</div>
            {!data?.activity?.length ? (
              <div style={{ color: '#475569', fontSize: 13 }}>No activity yet. Upload photos to see indexing in real-time.</div>
            ) : (
              <div style={{ fontFamily: 'monospace', fontSize: 12, overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '70px 16px 1fr 90px', gap: '4px 8px', color: '#475569', marginBottom: 6, fontSize: 10, borderBottom: '1px solid #334155', paddingBottom: 6, position: 'sticky', top: 0, background: '#1e293b' }}>
                  <span>TIME</span><span></span><span>ACTIVITY</span><span>EVENT</span>
                </div>
                {data.activity.map((item, idx) => (
                  <div key={idx} style={{
                    display: 'grid',
                    gridTemplateColumns: '70px 16px 1fr 90px',
                    gap: '4px 8px',
                    padding: '5px 0',
                    borderBottom: '1px solid #0f172a',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: '#475569', fontSize: 11 }}>{formatTime(item.ts)}</span>
                    <span style={{ fontSize: 12 }}>
                      {item.type === 'visitor' ? '👤' : (item.status === 'failed' ? '✗' : '✓')}
                    </span>
                    {item.type === 'visitor' ? (
                      <span style={{ color: '#34d399', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.full_name} registered
                        {item.match_count > 0 && <span style={{ color: '#f472b6', marginLeft: 6 }}>· {item.match_count} matches</span>}
                      </span>
                    ) : (
                      <span style={{ color: item.status === 'failed' ? '#fca5a5' : '#e2e8f0', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.file_name || '—'}
                        {item.faces > 0 && <span style={{ color: '#34d399', marginLeft: 6 }}>· {item.faces} face{item.faces !== 1 ? 's' : ''}</span>}
                      </span>
                    )}
                    <span style={{ color: '#475569', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.event_name || '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
